import { type ReactNode, useCallback, useEffect } from "react"
import { cn } from "@/lib/utils"

// A lightweight slide-in panel: left-anchored for the mobile admin nav drawer,
// bottom-anchored for the worker's day sheet. The app ships no modal package, so
// this mirrors the custom-overlay pattern used by the correction and confirm
// dialogs: a full-screen backdrop button plus an aria-labelled panel that closes
// on backdrop tap or Escape.
const SIDES = {
  left: "animate-sheet-left inset-y-0 left-0 w-72 max-w-[85vw]",
  bottom: "animate-sheet-up inset-x-0 bottom-0 max-h-[90vh] rounded-t-2xl",
} as const

export function Sheet({
  open,
  onClose,
  label,
  closeLabel,
  side = "left",
  className,
  children,
}: {
  open: boolean
  onClose: () => void
  label: string
  closeLabel: string
  side?: keyof typeof SIDES
  className?: string
  children: ReactNode
}) {
  const close = useCallback(onClose, [onClose])

  useEffect(() => {
    if (!open) {
      return
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") {
        close()
      }
    }
    document.addEventListener("keydown", onKey)
    return () => document.removeEventListener("keydown", onKey)
  }, [open, close])

  if (!open) {
    return null
  }

  return (
    <div className="fixed inset-0 z-50 print:hidden">
      <button
        type="button"
        aria-label={closeLabel}
        onClick={close}
        className="animate-overlay absolute inset-0 h-full w-full bg-black/40"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={label}
        className={cn(
          "absolute flex flex-col bg-card shadow-2xl motion-reduce:animate-none",
          SIDES[side],
          className,
        )}
      >
        {children}
      </div>
    </div>
  )
}
